/**
 * Classification comptable (Plan Comptable Général français)
 * Groupe les charges (Classe 6) et les produits (Classe 7) selon leur nature.
 */

// Classes de charges avec libellés et correspondances
export const CHARGE_CLASSES = [
  {
    code: '60',
    label: 'Achats',
    keywords: ['achat', 'fourniture', 'matière', 'marchandise', 'consommable', 'papeterie']
  },
  {
    code: '61',
    label: 'Services extérieurs',
    keywords: ['loyer', 'charge locative', 'assurance', 'entretien', 'maintenance', 'panneau', 'réparation', 'location']
  },
  {
    code: '62',
    label: 'Autres services extérieurs',
    keywords: ['marketing', 'publicité', 'communication', 'déplacement', 'transport', 'voyage', 'télécom', 'internet', 'téléphone', 'poste', 'honoraire', 'commission', 'sous-traitance']
  },
  {
    code: '63',
    label: 'Impôts, taxes et versements assimilés',
    keywords: ['impôt', 'taxe', 'mairie', 'tva', 'redevance', 'cotisation fiscale', 'patente', 'douane']
  },
  {
    code: '64',
    label: 'Charges de personnel',
    keywords: ['salaire', 'rémunération', 'paie', 'personnel', 'charge social', 'cnss', 'prime', 'indemnité']
  },
  {
    code: '65',
    label: 'Autres charges de gestion courante',
    keywords: ['redevance', 'droit', 'perte créance', 'subvention versée']
  },
  {
    code: '66',
    label: 'Charges financières',
    keywords: ['intérêt', 'agios', 'frais bancaire', 'change', 'escompte']
  },
  {
    code: '67',
    label: 'Charges exceptionnelles',
    keywords: ['exceptionnel', 'pénalité', 'amende', 'don']
  },
  {
    code: '68',
    label: 'Dotations aux amortissements',
    keywords: ['amortissement', 'dotation', 'dépréciation']
  }
];

// Classes de produits
export const PRODUIT_CLASSES = [
  {
    code: '70',
    label: 'Ventes et prestations de services',
    keywords: ['vente', 'prestation', 'location panneau', "chiffre d'affaires", 'facture', 'client', 'annonceur', 'orange', 'mtn', 'moov', 'ecobank', 'bsic', 'total', 'asky', 'oms', 'ledger']
  },
  {
    code: '74',
    label: "Subventions d'exploitation",
    keywords: ['subvention', 'aide', 'don reçu']
  },
  {
    code: '75',
    label: 'Autres produits de gestion courante',
    keywords: ['redevance reçue', 'loyer perçu', 'dividende', 'intérêt reçu']
  },
  {
    code: '76',
    label: 'Produits financiers',
    keywords: ['produit financier', 'plus-value', 'gain de change']
  },
  {
    code: '77',
    label: 'Produits exceptionnels',
    keywords: ['exceptionnel', 'cession', 'indemnité reçue']
  }
];

/**
 * Classe une entrée dans son groupe comptable en fonction de son libellé / catégorie.
 * @param {string} libelle - Libellé de la charge ou du produit (en minuscules acceptées)
 * @param {Array} classes - CHARGE_CLASSES ou PRODUIT_CLASSES
 * @returns {object} - Classe correspondante avec code + label
 */
export function classifyEntry(libelle, classes) {
  if (!libelle) return classes[classes.length - 1];
  const lower = libelle.toLowerCase();
  for (const cl of classes) {
    if (cl.keywords.some(kw => lower.includes(kw))) {
      return cl;
    }
  }
  // Défaut pour charges : 65, pour produits : 75
  return classes.find(c => c.code === '65' || c.code === '75') || classes[classes.length - 1];
}

/**
 * Regroupe un tableau d'entrées {label, amount} selon la classification comptable.
 * @param {Array} entries - [{ label, amount }, ...]
 * @param {Array} classes - CHARGE_CLASSES ou PRODUIT_CLASSES
 * @returns {Array} - [{ code, label, total, entries: [...] }, ...] triés par code
 */
export function groupByAccountingClass(entries, classes) {
  const grouped = {};
  entries.forEach(e => {
    const cls = classifyEntry(e.label, classes);
    if (!grouped[cls.code]) {
      grouped[cls.code] = { code: cls.code, label: cls.label, total: 0, entries: [] };
    }
    grouped[cls.code].total += parseFloat(e.amount) || 0;
    grouped[cls.code].entries.push(e);
  });
  return Object.values(grouped).sort((a, b) => a.code.localeCompare(b.code));
}
